import {naturalSort} from '@benchling/js-utils';
import {AxisScaleOutput} from '@visx/axis';
import {scaleBand, scaleLinear, scaleLog, scalePoint, scaleUtc, PickD3Scale} from '@visx/scale';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {DataType} from 'data-frames/DataFrameTypes';
import {VisxScaleType} from 'ui-library/charting/internals/VisxUtilityTypes';
import {
  AxisCompatibleDataType,
  AxisConfig,
  AxisScale,
  isContinuousAxisConfig,
} from 'ui-library/charting/Types';
import getDateDomain from 'ui-library/charting/util/getDateDomain';
import {omit} from 'util/omitKey';
import {assertNever} from 'util/Types';

import getTickLabelFormatter, {TickLabelFormatter} from './getTickLabelFormatter';
import {DEFAULT_SUGGESTED_NUM_TICKS} from './Theme';
import {validateAxis} from './Validation';

interface BaseVisxAxisConfig<TScaleType extends VisxScaleType, TDomainType> {
  domain: Array<TDomainType>;
  label?: string;
  // `number` is used to set nice to the number of suggestedNumTicks so we show the right number of ticks
  // when applying nice.
  // `false` is used to explicitly disable `nice`
  // `undefined` is used to rely on the scale's default or for scales that don't support `nice`
  nice?: number | false;
  scale: PickD3Scale<TScaleType, AxisScaleOutput>;
  suggestedNumTicks: number;
  tickLabelFormatter: TickLabelFormatter;
  tickLabelRotation?: number;
  type: TScaleType;
}

export interface ContinuousVisxAxisConfig<TScaleType extends VisxScaleType, TDomainType>
  extends BaseVisxAxisConfig<TScaleType, TDomainType> {
  zero: boolean;
}

export interface BandVisxAxisConfig extends BaseVisxAxisConfig<VisxScaleType.BAND, string> {}
export interface LinearVisxAxisConfig extends ContinuousVisxAxisConfig<VisxScaleType.LINEAR, number> {}
export interface LogVisxAxisConfig extends ContinuousVisxAxisConfig<VisxScaleType.LOG, number> {}
export interface PointVisxAxisConfig extends ContinuousVisxAxisConfig<VisxScaleType.POINT, string> {}
export interface UTCVisxAxisConfig extends ContinuousVisxAxisConfig<VisxScaleType.UTC, Date> {}

export type VisxAxisConfig =
  | BandVisxAxisConfig
  | LinearVisxAxisConfig
  | LogVisxAxisConfig
  | PointVisxAxisConfig
  | UTCVisxAxisConfig;

interface VisxScaleTypeToAxisConfigType {
  [VisxScaleType.BAND]: BandVisxAxisConfig;
  [VisxScaleType.LINEAR]: LinearVisxAxisConfig;
  [VisxScaleType.LOG]: LogVisxAxisConfig;
  [VisxScaleType.POINT]: PointVisxAxisConfig;
  [VisxScaleType.UTC]: UTCVisxAxisConfig;
}

const NUMERIC_AXIS_SCALE_TO_VISX_SCALE_TYPE: {[key in AxisScale]: VisxScaleType} = {
  [AxisScale.LINEAR]: VisxScaleType.LINEAR,
  [AxisScale.LOG]: VisxScaleType.LOG,
};

const NON_NUMERIC_AXIS_TYPE_TO_VISX_SCALE_TYPE: {
  [key in Exclude<AxisCompatibleDataType, DataType.NUMBER>]: VisxScaleType;
} = {
  [DataType.DATE_OR_TIMESTAMP]: VisxScaleType.UTC,
  [DataType.STRING]: VisxScaleType.POINT,
};

type ExtentOverride = [number | null, number | null];

export interface ExtraAxisConfigOptions {
  extentOverride?: ExtentOverride;
  sortDomain?: boolean;
  suggestedNumTicks?: number;
}

/**
 * Translate an AxisConfig to a VisxAxisConfig.
 *
 * AxisConfig is our interface for configuring axes that the charting module exposes, while VisxAxisConfig
 * matches the interface that Visx expects to configure its axes. This function translates between the two,
 * and adds additional fields to make the axes display nicely.
 *
 * Raises AssertionError if the input axis is not valid with the input axisType.
 */
export default function getVisxAxisConfig(
  axisConfig: AxisConfig,
  axisType: AxisCompatibleDataType,
  values: Array<unknown>,
  {extentOverride, sortDomain = false, suggestedNumTicks}: ExtraAxisConfigOptions = {}
): VisxAxisConfig {
  validateAxis(axisConfig, axisType);
  const scaleType = getVisxScaleType(axisConfig, axisType);
  const baseConfig = {
    ...(axisConfig.hideLabel ? {} : {label: axisConfig.label}),
    ...(isContinuousAxisConfig(axisConfig) ? {zero: axisConfig.includeZero} : {}),
    nice:
      extentOverride == null || isEqual(extentOverride, [null, null])
        ? (suggestedNumTicks ?? DEFAULT_SUGGESTED_NUM_TICKS)
        : false,
    suggestedNumTicks: suggestedNumTicks ?? DEFAULT_SUGGESTED_NUM_TICKS,
    type: scaleType,
  };

  const domain = getDomain(axisConfig, axisType, values, extentOverride);

  switch (scaleType) {
    case VisxScaleType.BAND: {
      let maybeSortedDomain = domain as Array<string | number>;
      if (sortDomain) {
        maybeSortedDomain =
          axisType === DataType.NUMBER
            ? (_.sortBy(domain) as Array<number>)
            : naturalSort(domain as Array<string>);
      }

      // Band axes assume the domain is strings for correct rendering, so convert to string after we have
      // correctly sorted the domain.
      maybeSortedDomain = maybeSortedDomain.map((value) => String(value));

      const scale = scaleBand({domain: maybeSortedDomain});
      const bandScaleSuggestedNumTicks = suggestedNumTicks ?? domain?.length;

      return {
        ...baseConfig,
        domain: maybeSortedDomain,
        scale,
        suggestedNumTicks: bandScaleSuggestedNumTicks,
        tickLabelFormatter: getTickLabelFormatter(scaleType, scale, bandScaleSuggestedNumTicks),
      } as BandVisxAxisConfig;
    }
    case VisxScaleType.LINEAR: {
      // XYChart uses the separately passed domain and the output of <*Series> components to
      // determine axis bounds, not the domain or range of the scale object. The domain of the scale object
      // cannot be used to extend the axes beyond the points plotted by <*Series> components.
      const scale = scaleLinear<AxisScaleOutput>({domain: domain as Array<number>, nice: baseConfig.nice});
      return {
        ...baseConfig,
        domain,
        scale,
        tickLabelFormatter: getTickLabelFormatter(scaleType, scale, baseConfig.suggestedNumTicks),
      } as LinearVisxAxisConfig;
    }
    case VisxScaleType.LOG: {
      assertAndContinue(
        suggestedNumTicks === undefined,
        'Cannot specify suggested num ticks for log-scale axes'
      );
      const scale = scaleLog<AxisScaleOutput>({domain: domain as Array<number>});
      let lowerBound = Math.log10(Math.abs(scale.domain()[0]));
      let upperBound = Math.log10(Math.abs(scale.domain()[1]));

      if (lowerBound > upperBound) {
        [lowerBound, upperBound] = [upperBound, lowerBound];
      }

      // By default `d3-scale` log scales compute many tick values between each jump of 10. For example, between
      // 1 and 10, it will compute 1, 2, 3 ..., 8, 9, 10, meaning it doesn't really follow the suggested number
      // of ticks. This tends to create ugly charts with too many tick values in our product.
      //
      // In `d3-scale`'s source code for log's `ticks()`, there is a branching condition where if
      // `log(domain upper bound) - log(domain lower bound) < suggestedNumTicks`, it uses `d3-array`'s `ticks()`
      // function, which creates uniformly-spaced ticks on the log of domain bounds. This produces a much better
      // chart in the product, so we calculate `suggestedNumTicks` such that we always use that behavior.
      //
      // Since many Visx primitives read `scale.ticks()` directly, we have to rely on of `d3-scale`'s `ticks()`
      // behavior instead of us calling `d3-array`'s `ticks()` directly.
      //
      // However, there is an issue with this approach if the suggested number of ticks ends up being less than 1.
      // For example, in the case where we have values that are very close to eachother (e.g. 1, 2, 3, 4 vs expected log
      // scaled values 1, 10, 100, 1000). In this case, this uniform distribution ends making it so there's no ticks on
      // the axis, which crashes some axis rendering code and removes the axis title. To go around this, we set the minimum
      // amount of log suggested ticks to 2.
      const logSuggestedNumTicks = Math.max(upperBound - lowerBound, 2);

      return {
        ...omit(baseConfig, 'nice'),
        domain,
        scale,
        suggestedNumTicks: logSuggestedNumTicks,
        tickLabelFormatter: getTickLabelFormatter(scaleType, scale, logSuggestedNumTicks),
      } as LogVisxAxisConfig;
    }
    case VisxScaleType.POINT: {
      const maybeSortedDomain = sortDomain ? naturalSort(domain as Array<string>) : (domain as Array<string>);
      const scale = scalePoint({domain: maybeSortedDomain});
      const pointScaleSuggestedNumTicks = suggestedNumTicks ?? domain?.length;

      return {
        ...baseConfig,
        domain: maybeSortedDomain,
        scale,
        suggestedNumTicks: pointScaleSuggestedNumTicks,
        tickLabelFormatter: getTickLabelFormatter(scaleType, scale, pointScaleSuggestedNumTicks),
      } as PointVisxAxisConfig;
    }
    case VisxScaleType.UTC: {
      const scale = scaleUtc<AxisScaleOutput>({domain: domain as Array<Date>, nice: baseConfig.nice});

      return {
        ...baseConfig,
        domain,
        scale,
        tickLabelFormatter: getTickLabelFormatter(scaleType, scale, baseConfig.suggestedNumTicks),
      } as UTCVisxAxisConfig;
    }
    default:
      assert(false, `Invalid scale type ${assertNever(scaleType)}`);
  }
}

function getVisxScaleType(axisConfig: AxisConfig, axisType: AxisCompatibleDataType): VisxScaleType {
  if (!isContinuousAxisConfig(axisConfig)) {
    return VisxScaleType.BAND;
  }

  switch (axisType) {
    case DataType.NUMBER:
      return NUMERIC_AXIS_SCALE_TO_VISX_SCALE_TYPE[axisConfig.scale];
    default:
      return NON_NUMERIC_AXIS_TYPE_TO_VISX_SCALE_TYPE[axisType];
  }
}

function getDomain(
  axisConfig: AxisConfig,
  axisType: DataType,
  axisValues: Array<unknown>,
  extentOverride?: ExtentOverride
): Array<unknown> {
  assertAndContinue(
    extentOverride == null || axisType === DataType.NUMBER,
    'Extent can only be overridden when axisType is NUMBER'
  );

  switch (axisType) {
    case DataType.DATE_OR_TIMESTAMP: {
      return getDateDomain(axisValues as Array<Date>);
    }
    case DataType.NUMBER: {
      if (!isContinuousAxisConfig(axisConfig)) {
        return _.unique(axisValues);
      }

      const minValue =
        extentOverride != null && extentOverride[0] != null
          ? extentOverride[0]
          : Math.min(...(axisValues as Array<number>));

      const maxValue =
        extentOverride != null && extentOverride[1] != null
          ? extentOverride[1]
          : Math.max(...(axisValues as Array<number>));

      if (!axisConfig.includeZero) {
        return [minValue, maxValue];
      } else {
        return [Math.min(0, minValue), Math.max(0, maxValue)];
      }
    }
    case DataType.STRING:
      return _.unique(axisValues);
    default:
      throw new Error(`Unexpected axis type ${axisType}`);
  }
}

export function isVisxAxisConfigScaleType<TScaleType extends VisxScaleType>(
  visxAxisConfig: VisxAxisConfig,
  scaleType: TScaleType
): visxAxisConfig is VisxScaleTypeToAxisConfigType[TScaleType] {
  return visxAxisConfig.type === scaleType;
}

export function assertVisxAxisConfigScaleType<TScaleType extends VisxScaleType>(
  visxAxisConfig: VisxAxisConfig,
  scaleType: TScaleType
): asserts visxAxisConfig is VisxScaleTypeToAxisConfigType[TScaleType] {
  assert(
    isVisxAxisConfigScaleType(visxAxisConfig, scaleType),
    `Expected visx axis config to match type ${scaleType}`
  );
}
